import styled from 'styled-components';
import {
  mqSmallMax,
  mqLargeMin,
  mqSXLargeMin,
} from 'styled/tools/media-queries';
import SiteAccordion from 'components/site/site-accordion';
import { Fontw35s40h48, Fontw35s72h80, Fontw4s16h24 } from 'styles/commonCss';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { createMarkup } from 'lib/utils/create-markup';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import RawHtmlVideo from 'components/pdp/raw-html-video';

// Mirrors the DS-01 PDP essentials mini banner (Shopify `essentials_module` metaobject).
const MINI_BANNER_IMAGE =
  'https://cdn.shopify.com/s/files/1/0929/7828/2785/files/ds-tal.webp?v=1773682254';
const MINI_BANNER_VIDEO =
  'https://cdn.shopify.com/videos/c/o/v/86df104a49fa418288ea51d5c093a744.mov';
const MINI_BANNER_VIDEO_FALLBACK =
  'https://cdn.shopify.com/videos/c/vp/86df104a49fa418288ea51d5c093a744/86df104a49fa418288ea51d5c093a744.HD-720p-1.6Mbps-80056925.mp4';

const BannerTile = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 32px;
  flex: 1;

  @media ${mqSmallMax} {
    aspect-ratio: 1;
    flex: none;
  }
`;

const BannerTileImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BannerTileVideo = styled.div`
  video {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 100%;
    max-height: 100%;
  }
`;

const Section = styled.section`
  justify-content: space-between;
  align-items: start;
  background-color: var(--fill-primary);
  display: flex;
  padding: 48px 48px;
  gap: 24px;

  @media ${mqSmallMax} {
    flex-direction: column;
    padding: 48px 24px;
    gap: 0px;
  }
`;

const LeftColumn = styled.div`
  width: 50%;
  align-self: stretch;

  @media ${mqSmallMax} {
    width: 100%;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  height: 100%;
`;

const Content = styled.div`
  flex-grow: 1;
  flex-basis: 0%;
  flex-direction: column;
  display: flex;
  margin: 0 0 0 0;

  @media (max-width: 991px) {
    max-width: 100%;
  }

  @media ${mqLargeMin} {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  /* Only the stacked tile sits in this column; the desktop one is in ImageContainer. */
  > ${BannerTile} {
    margin-top: 24px;

    @media ${mqSmallMax} {
      margin-bottom: 24px;
    }
  }
`;

const Title = styled.h1`
  ${Fontw35s72h80}
  color: var(--text-primary);
  letter-spacing: -1.44px;

  @media ${mqSmallMax} {
    ${Fontw35s40h48}
    letter-spacing: -0.4px;
    max-width: 300px;
  }
`;

const Description = styled.div`
  ${Fontw4s16h24}
  margin-top: 48px;
  margin-bottom: 48px;

  @media ${mqSmallMax} {
    margin-top: 0px;
  }
`;

const AccordionContainer = styled.div`
  .site-accordion p {
    color: var(--text-primary);
  }

  .site-accordion ul {
    padding-left: 10px;
    padding-right: 10px;
    list-style-type: inherit;
  }

  .site-accordion ul li p {
    display: inline;
  }
`;

const MiniBannerTile = () => (
  <BannerTile>
    <BannerTileImage src={MINI_BANNER_IMAGE} alt='' loading='lazy' />
    <BannerTileVideo>
      <RawHtmlVideo
        src={MINI_BANNER_VIDEO}
        backupSrc={MINI_BANNER_VIDEO_FALLBACK}
      />
    </BannerTileVideo>
  </BannerTile>
);

const Benefits = ({ benefits, description }) => {
  const isSmall = !useClientSideMediaQuery(mqSXLargeMin);

  return (
    <Section className='benefits-section'>
      {!isSmall && (
        <LeftColumn>
          <ImageContainer className='capsule-container-desktop'>
            <MiniBannerTile />
          </ImageContainer>
        </LeftColumn>
      )}
      <Content>
        <Title>
          DS-01® Daily Synbiotic is now available for your healthcare practice.
        </Title>
        {isSmall ? <MiniBannerTile /> : null}
        {description && (
          <Description
            dangerouslySetInnerHTML={createMarkup(
              documentToHtmlString(description),
            )}
          />
        )}
        <AccordionContainer>
          {Object.keys(benefits).map((benefit, index) => (
            <SiteAccordion
              key={benefit}
              title={benefit}
              hasContentLeft
              hasSmallSpacing
              hasLargeTitle={!isSmall}
              overwriteTitleColor='green'
              content={benefits[benefit]}
              index={index}
            />
          ))}
        </AccordionContainer>
      </Content>
    </Section>
  );
};

export default Benefits;
